// ui-bootstrap's `$uibModalStack`, reduced to what the screen shows: ONE backdrop for however many
// modals are open, sitting just beneath the topmost one.
//
// ⚠️ TWO BACKDROPS STACK TO A DARKER GREY, which is what this port drew when the network-error
// alert opened over the Exchange dialogue (each `.modal-backdrop.in` is black at 0.5). uib keeps a
// single backdrop and raises its z-index with the stack instead: window `1050 + 10·index`,
// backdrop `1040 + 10·index` of the TOP modal only. Measured against the dev tenant, 28 Oct.
import { useEffect, useId, useSyncExternalStore } from 'react'

let stack: string[] = []
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of [...listeners]) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export interface ModalLayer {
  /** z-index for the window, as uib computes it. */
  windowZIndex: number
  /** Present only while this modal is the top one — the single shared backdrop. */
  backdropZIndex?: number
}

/** Register a modal while `open`; answers where it sits in the stack. */
export function useModalLayer(open: boolean): ModalLayer {
  const id = useId()
  useEffect(() => {
    if (!open) return undefined
    stack = [...stack, id]
    emit()
    return () => {
      stack = stack.filter((entry) => entry !== id)
      emit()
    }
  }, [open, id])
  const snapshot = useSyncExternalStore(subscribe, () => stack, () => stack)
  const index = snapshot.indexOf(id)
  // Before the effect has run (first render) the modal is not in the stack yet; it is about to be
  // the top one, which is also what it will be once registered.
  const position = index === -1 ? snapshot.length : index
  const top = index === -1 || index === snapshot.length - 1
  return {
    windowZIndex: 1050 + 10 * position,
    ...(top ? { backdropZIndex: 1040 + 10 * position } : {}),
  }
}
